import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { defaultDatePickerStrings } from "@fluentui/react";
import { CloudArrowDownRegular } from "@fluentui/react-icons";
import { toast } from "components/FluentToast";
import { VSpace } from "components/Spacer";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useImportedInvoicesStore } from "store/ImportedInvoicesStore";
import { ImportedInvoiceImportResult } from "types/importedInvoices";
import { ImportResultReport } from "./ImportResultReport";

const PULL_DEFAULT_DAYS = 7; // the dialog proposes the last week
const PULL_MAX_WINDOW_MONTHS = 1; // the BFF refuses a longer period (same rule)

const formatDate = (date?: Date): string =>
  !date ? "" : date.getDate() + "." + (date.getMonth() + 1) + "." + date.getFullYear();

// Pulls the invoices QTG issued in a date window (PATCH importedinvoice/pull): both dates are
// mandatory, proposed as the last 7 days up to today. The rows take the same path as an
// uploaded file. The report stays visible until the dialog is closed.
export const PullImportedInvoicesDialog = () => {
  const [
    { pullImportedInvoicesDialog, pullImportedInvoicesStatus, lastPullResult },
    {
      setImportedInvoicesStoreValues,
      pullImportedInvoices,
      applyImportedInvoices,
      loadImportedInvoices,
    },
  ] = useImportedInvoicesStore();
  const isLoading = pullImportedInvoicesStatus === "loading";
  const { t } = useTranslation();
  const result: ImportedInvoiceImportResult | null = lastPullResult;
  const [issuedFrom, setIssuedFrom] = useState<Date | null>(
    moment().subtract(PULL_DEFAULT_DAYS, "days").startOf("day").toDate()
  );
  const [issuedTo, setIssuedTo] = useState<Date | null>(moment().startOf("day").toDate());
  const windowTooLong =
    !!issuedFrom &&
    !!issuedTo &&
    moment(issuedTo).isAfter(moment(issuedFrom).add(PULL_MAX_WINDOW_MONTHS, "months"), "day");
  const windowInvalid =
    !issuedFrom || !issuedTo || moment(issuedTo).isBefore(moment(issuedFrom), "day") || windowTooLong;

  const close = () => {
    setImportedInvoicesStoreValues({
      pullImportedInvoicesDialog: { isOpen: false },
      lastPullResult: null,
    });
  };

  const headline = result
    ? t("pct.pullImportedInvoicesResult.label", {
        rows: result.pulledRowCount ?? 0,
        from: result.pulledIssuedFrom ? moment(result.pulledIssuedFrom).format("DD.MM.YYYY") : "",
        to: result.pulledIssuedTo ? moment(result.pulledIssuedTo).format("DD.MM.YYYY") : "",
      })
    : undefined;

  return (
    <Dialog open={pullImportedInvoicesDialog.isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t("pct.pullImportedInvoices.label")}</DialogTitle>
          <DialogContent>
            <VSpace height={10} />
            <MessageBar intent="info" data-help="imported-invoices-pull-info">
              <MessageBarBody>{t("pct.pullImportedInvoicesInfo.label")}</MessageBarBody>
            </MessageBar>
            <VSpace height={15} />
            {result ? (
              <div data-help="imported-invoices-pull-result">
                <ImportResultReport result={result} headline={headline} />
              </div>
            ) : (
              <>
                {/* the two dates stay on one row (fixed widths, no wrap); the message goes below the row so the row never breaks */}
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <Field label={t("pct.pullImportedInvoicesIssuedFrom.label")} required>
                    <DatePicker
                      data-help="imported-invoices-pull-from-date"
                      value={issuedFrom}
                      onSelectDate={(date) => setIssuedFrom(date ?? null)}
                      strings={defaultDatePickerStrings}
                      formatDate={formatDate}
                      disabled={isLoading}
                      style={{ width: "160px" }}
                    />
                  </Field>
                  <Field
                    label={t("pct.pullImportedInvoicesIssuedTo.label")}
                    required
                    validationState={windowInvalid && issuedFrom && issuedTo ? "error" : "none"}
                  >
                    <DatePicker
                      data-help="imported-invoices-pull-to-date"
                      value={issuedTo}
                      onSelectDate={(date) => setIssuedTo(date ?? null)}
                      strings={defaultDatePickerStrings}
                      formatDate={formatDate}
                      disabled={isLoading}
                      style={{ width: "160px" }}
                    />
                  </Field>
                </div>
                {windowInvalid && issuedFrom && issuedTo ? (
                  <div style={{ marginTop: 8, color: "var(--colorPaletteRedForeground1)", fontSize: "12px" }}>
                    {t("pct.pullImportedInvoicesWindowInvalid.label", { months: PULL_MAX_WINDOW_MONTHS })}
                  </div>
                ) : null}
              </>
            )}
            <VSpace height={20} />
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={isLoading} onClick={close}>
              {t("pct.close.label")}
            </Button>
            {!result ? (
              <Button
                data-help="imported-invoices-pull-submit"
                appearance="primary"
                disabled={isLoading || windowInvalid}
                icon={isLoading ? <Spinner size="tiny" /> : <CloudArrowDownRegular fontSize={16} />}
                onClick={() => {
                  if (windowInvalid) return;
                  pullImportedInvoices(
                    {
                      issuedFrom: moment(issuedFrom).format("YYYY-MM-DD"),
                      issuedTo: moment(issuedTo).format("YYYY-MM-DD"),
                    },
                    (pullResult: ImportedInvoiceImportResult) => {
                      toast.success(t("greco.success"));
                      applyImportedInvoices({ importedInvoices: pullResult.importedInvoices, t });
                      // the new rows outside the loaded range come with a reload
                      loadImportedInvoices({ t });
                    }
                  );
                }}
              >
                {t("pct.pull.label")}
              </Button>
            ) : null}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
